import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useOutletContext } from "react-router-dom";
import {
  Body1,
  Body2,
  Button,
  Caption1,
  EmptySlate,
  SettingsIcon,
  Tooltip,
  WarningTriangleIcon,
} from "@activecollab/components";
import classNames from "classnames";
import PropTypes from "prop-types";
import {
  STATUS_GLOBAL_DAY_OFF,
  STATUS_NON_WORKING_DAYS,
  STATUS_NOT_AVAILABLE,
  STATUS_USER_DAY_OFF,
} from "~/components/Capacity";
import { CapacityLoader } from "~/components/Capacity/CapacityLoader";
import { UsersColumn } from "~/components/Capacity/UsersColumn";
import { UtilizationLegend } from "~/components/Capacity/UtilizationLegend";
import { getProgressColor, UtilizationPie } from "~/components/Capacity/UtilizationPie";
import { useUtilization } from "~/repository";
import { getPersonalizedConfigOption, getRawPersonalizedConfigOption } from "~/store/selectors/selector.configOptions";
import { getUsersByIds } from "~/store/selectors/selector.users";
import { minutesToHours } from "~/utils/utils";
import { UtilizationDialog } from "./UtilizationDialog";

const handleNoUtilization = (status, trackedTime) => {
  return (
    (status === STATUS_NOT_AVAILABLE || status === STATUS_NON_WORKING_DAYS || status === STATUS_GLOBAL_DAY_OFF) &&
    trackedTime === 0
  );
};

const Warning = ({ capacity, trackedTime }) => {
  const { t } = useTranslation();
  return (
    <Tooltip
      title={
        <>
          <div className="tw-flex tw-items-center tw-justify-between tw-my-1">
            <div className="tw-mr-3">{t("Available:")}</div>
            <div>{minutesToHours(capacity, true)}</div>
          </div>
          <div className="tw-flex tw-items-center tw-justify-between tw-my-1">
            <div className="tw-mr-3">{t("Tracked:")}</div>
            <div>{minutesToHours(trackedTime, true)}</div>
          </div>
        </>
      }
    >
      <WarningTriangleIcon
        fill="var(--red-alert)"
        width={16}
        height={16}
      />
    </Tooltip>
  );
};

Warning.propTypes = {
  capacity: PropTypes.number.isRequired,
  trackedTime: PropTypes.number.isRequired,
};

export const UtilizationTable = () => {
  const { t } = useTranslation();
  const capacityFilterObject = useSelector((state) => getPersonalizedConfigOption(state, "capacity_filter")).toJS();
  const utilizationLevels = useSelector((state) => getRawPersonalizedConfigOption(state, "utilization_levels"));
  const showOnlyBillable = useSelector((state) =>
    getRawPersonalizedConfigOption(state, "utilization_show_only_billable_hours")
  );
  const { from, to, setRouteQueryState } = useOutletContext();
  const [openDialog, setOpenDialog] = useState(false);

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const { data, isFetching, isError } = useUtilization({
    from,
    to,
    scale: capacityFilterObject?.scale ?? "weekly",
    user_ids: capacityFilterObject?.users ?? [],
    show_only_billable: showOnlyBillable,
  });

  const {
    users_data = [],
    columns = [],
    current_period,
    total_utilization,
    total_capacity,
    total_tracked_time,
  } = data || {};

  const userIds = users_data.map((user) => user.user_id);
  const users = useSelector((state) => getUsersByIds(state, userIds));

  useEffect(() => {
    setRouteQueryState({
      isFetching,
      isError: data ? isError : false,
      hasUsers: users_data.length > 0,
    });
  }, [data, isError, isFetching, setRouteQueryState, users_data.length]);

  const sortedUsers = [...users_data].sort((a, b) => {
    const aName = users.get(`${a.user_id}`)?.get("display_name")?.toLowerCase() ?? "";
    const bName = users.get(`${b.user_id}`)?.get("display_name")?.toLowerCase() ?? "";

    return aName.localeCompare(bName);
  });

  if (isFetching) {
    return <CapacityLoader />;
  }

  const hasData = users_data.length > 0 && (data ? !isError : true);

  return hasData ? (
    <>
      <div>
        <div className="tw-flex tw-w-full tw-min-w-0">
          <UsersColumn
            usersData={sortedUsers}
            context="utilization-user-link"
            isFetching={isFetching}
          />

          <div className="tw-min-w-0 tw-overflow-x-auto tw-flex-grow">
            <div className="tw-min-w-max">
              <div className="tw-flex">
                {columns.map((col) => (
                  <div
                    key={col.key}
                    className="tw-flex-[1_0_140px] tw-flex tw-flex-col [&:last-child>div:last-child]:tw-border-r-0"
                  >
                    <Caption1
                      className={classNames(
                        {
                          "capacity-current-period-column tw-bg-primary-300": col.key === current_period,
                        },
                        "tw-h-10 tw-w-full tw-flex tw-items-center tw-justify-center tw-border-b tw-border-border-primary tw-border-solid tw-border-0"
                      )}
                      weight="bold"
                      color="secondary"
                    >
                      {col.label}
                    </Caption1>
                    <Body1
                      style={{
                        backgroundColor: col.disabled
                          ? "var(--color-primary-200)"
                          : getProgressColor(
                              col.total_utilization,
                              utilizationLevels?.get("low"),
                              utilizationLevels?.get("high"),
                              utilizationLevels?.get("critical"),
                              true
                            ),
                      }}
                      weight="bold"
                      className="tw-h-10 tw-w-full tw-flex tw-items-center tw-justify-center tw-border-r tw-border-b tw-border-border-primary tw-border-solid tw-border-0"
                    >
                      {col.disabled ? (
                        "-"
                      ) : col.total_tracked_time && !col.total_capacity ? (
                        <Warning
                          capacity={col.total_capacity}
                          trackedTime={col.total_tracked_time}
                        />
                      ) : (
                        `${col.total_utilization} %`
                      )}
                    </Body1>
                  </div>
                ))}
              </div>

              {sortedUsers.map((user, index) => (
                <div
                  key={user.user_id}
                  className="tw-flex"
                >
                  {columns.map((col) => {
                    const cell = user.columns_data[col.key];

                    return cell ? (
                      <Body1
                        key={col.key}
                        weight="bold"
                        color={cell.capacity > 0 && cell.status !== STATUS_USER_DAY_OFF ? "primary" : "alert"}
                        className={classNames(
                          {
                            "tw-bg-theme-200": index % 2 === 0,
                            "capacity-disabled-day": handleNoUtilization(cell.status, cell.tracked_time),
                          },
                          "tw-h-10 tw-flex-[1_0_140px] tw-flex tw-items-center tw-justify-center tw-border-r tw-border-b tw-border-border-primary tw-border-solid tw-border-0 last:tw-border-r-0"
                        )}
                      >
                        {handleNoUtilization(cell.status, cell.tracked_time) ? null : (
                          <>
                            {cell.status === STATUS_USER_DAY_OFF && cell.tracked_time === 0 ? (
                              "OFF"
                            ) : (
                              <>
                                {minutesToHours(cell.tracked_time, true)}/{minutesToHours(cell.capacity, true)}{" "}
                                <UtilizationPie
                                  utilization={cell.tracked_time && !cell.capacity ? 101 : cell.utilization} // make red dot if there is no capacity, but has tracked time
                                  className="tw-ml-1.5"
                                />{" "}
                              </>
                            )}
                          </>
                        )}
                      </Body1>
                    ) : null;
                  })}
                </div>
              ))}
            </div>
          </div>

          <div className="tw-w-[104px] tw-shrink-0">
            <Caption1
              weight="bold"
              color="secondary"
              className="tw-h-10 tw-flex tw-items-center tw-justify-center tw-border-b tw-border-border-primary tw-border-solid tw-border-0"
            >
              {t("Total")}
            </Caption1>

            <Body1
              weight="bold"
              className="tw-h-10 tw-flex tw-items-center tw-justify-center tw-border-b tw-border-l tw-border-border-primary tw-border-solid tw-border-0"
              style={{
                backgroundColor: getProgressColor(
                  total_utilization,
                  utilizationLevels?.get("low"),
                  utilizationLevels?.get("high"),
                  utilizationLevels?.get("critical"),
                  true
                ),
              }}
            >
              {!total_capacity && total_tracked_time ? (
                <Warning
                  capacity={total_capacity}
                  trackedTime={total_tracked_time}
                />
              ) : (
                total_utilization + " %"
              )}
            </Body1>

            {sortedUsers.map((user) => (
              <Body1
                key={user.user_id}
                weight="bold"
                className="tw-h-10 tw-flex tw-items-center tw-justify-center tw-border-b tw-border-l tw-border-border-primary tw-border-solid tw-border-0"
                style={{
                  backgroundColor: getProgressColor(
                    user.total_utilization,
                    utilizationLevels?.get("low"),
                    utilizationLevels?.get("high"),
                    utilizationLevels?.get("critical"),
                    true
                  ),
                }}
              >
                {!user.total_capacity && user.total_tracked_time ? (
                  <Warning
                    capacity={user.total_capacity}
                    trackedTime={user.total_tracked_time}
                  />
                ) : (
                  user.total_utilization + " %"
                )}
              </Body1>
            ))}
          </div>
        </div>
        <div className="tw-flex tw-items-center tw-gap-6 tw-justify-end tw-my-4 tw-mr-4">
          <UtilizationLegend className="lg:tw-flex tw-hidden" />
          <Tooltip title={t("Setup")}>
            <Button
              onClick={handleOpenDialog}
              variant="text gray"
              className="tw-flex tw-items-center tw-gap-1.5"
            >
              <Body2
                weight="medium"
                color="secondary"
              >
                {t("Setup")}
              </Body2>
              <SettingsIcon
                width={18}
                height={18}
                fill="var(--color-theme-700)"
              />
            </Button>
          </Tooltip>
        </div>
      </div>
      <UtilizationDialog
        open={openDialog}
        onClose={handleCloseDialog}
      />
    </>
  ) : (
    <EmptySlate
      title={t("No Data")}
      src="/assets/environment/images/layout/search_empty_page.png"
      alt="Utilization"
      description={t("Adjust your filters and run the report again.")}
      className="tw-my-auto"
    />
  );
};
